import React from 'react';
import './ReportV5.css';
import ReportIcon from './ReportIcons';
import ReportV5Radar from './ReportV5Radar';
import ReportShell, { ReportDownloadButton, ReportProHint } from './ReportShell';
import QuestionAnalysisSection from './QuestionAnalysisSection';
import RetestLauncher from './RetestLauncher';
import RetestComparison from './RetestComparison';

// 目录条目：id 必须与下方各 Page 的 <section id> 一一对应（scroll-spy 靠它）
const BASIC_SECTIONS = [
  { id: 'v5r-sec-overview', label: '结果总览' },
  { id: 'v5r-sec-structure', label: '能力结构' },
  { id: 'v5r-sec-action', label: '行动建议' },
];
const INTERACTIVE_SECTIONS = [
  ...BASIC_SECTIONS,
  { id: 'v5r-sec-analysis', label: '逐题复盘' },
];

/**
 * V5 粗略报告主组件。
 * 入参：
 *  - vm:            adapter 转出的 view model（结构见 adapters/reportV5Adapter.js）
 *  - assessmentId:  当前测评 id（'preview' 时 PDF 下载按钮禁用）
 *  - onUnlock:      () => void   点击"解锁详细报告" CTA 时触发
 *  - printMode:     boolean  Playwright PDF 印刷模式 —— 根容器加 .print-mode 类
 *                            供 print.css 接管样式；CTA 卡片 / PDF 下载按钮整体
 *                            不渲染（PDF 内部不该有"下载 PDF"按钮）。
 *
 * 3 个 Page 顺序流式排列（同一卷轴）：结果总览 / 能力结构 / 行动建议。
 * 顶部 fallback 提示由 vm.is_fallback 触发；PDF 下载按钮调用 api/reports.downloadReportV5PDF。
 */
export default function ReportV5({
  vm, assessmentId, onUnlock, onBack, onRetestReady, printMode = false,
}) {
  const interactiveSections = vm.retest_comparison?.available
    ? [...BASIC_SECTIONS, { id: 'v5r-sec-comparison', label: '复测对比' }, { id: 'v5r-sec-analysis', label: '逐题复盘' }]
    : INTERACTIVE_SECTIONS;
  return (
    <div className={`v5r-root${printMode ? ' print-mode' : ''}`}>
      <ReportShell
        sections={printMode ? BASIC_SECTIONS : interactiveSections}
        printMode={printMode}
        footer={
          <>
            <ReportDownloadButton assessmentId={assessmentId} variant="rough" />
            <ReportProHint />
          </>
        }
      >
        <div className="v5r-container">
          {onBack && !printMode && <BackBar onBack={onBack} />}
          {vm.is_fallback && <FallbackBanner />}
          <Page1 vm={vm} />
          <Page2 vm={vm} />
          <Page3 vm={vm} />
          {!printMode && (
            <RetestComparison
              comparison={vm.retest_comparison}
              learningEntry={vm.learning_platform}
              reportActions={vm.report_actions}
            />
          )}
          {!printMode && (
            <RetestLauncher
              assessmentId={assessmentId}
              journey={vm.retest_journey}
              reportActions={vm.report_actions}
              learningEntry={vm.learning_platform}
              improvements={vm.priority_improvements}
              onRetestReady={onRetestReady}
            />
          )}
          {!printMode && (
            <QuestionAnalysisSection
              assessmentId={assessmentId}
              analysisAccess={vm.analysis_access}
            />
          )}
          {/* 「查看专业版报告」CTA 卡片留在正文末尾（现有流程），目录底部的引导文字指向此处 */}
          {!printMode && (
            <DetailedUnlockCTA unlocked={vm.detailed.unlocked} onUnlock={onUnlock} />
          )}
        </div>
      </ReportShell>
    </div>
  );
}

function BackBar({ onBack }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 14px',
          background: '#fff',
          border: '1px solid var(--v5-line)',
          borderRadius: 10,
          color: 'var(--v5-ink-2)',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(15, 35, 80, 0.04)',
        }}
      >
        <ReportIcon name="arrow-left" size={16} />
        <span>返回历史记录</span>
      </button>
    </div>
  );
}

// ===========================================================================
// 共享子组件
// ===========================================================================

function FallbackBanner() {
  return (
    <div className="v5r-fallback-banner">
      <ReportIcon name="lightbulb" size={18} />
      <div>
        <b>降级版本：</b>本次报告由后端 fallback 模板生成（LLM 输出未通过硬校验）。
        分数与排名均为真实计算结果，文案为模板兜底，建议联系管理员重新生成。
      </div>
    </div>
  );
}

// 页头右上角装饰插画（3 页各一主题：总览=条形 / 能力结构=网格 / 行动建议=靶心）。
// 绝对定位到卡片右上角（见 .v5r-head-illus），不占页头行高。
function HeaderIllus({ variant }) {
  const tileId = `v5r-tile-${variant}`;
  const discId = `v5r-disc-${variant}`;
  return (
    <svg width="104" height="78" viewBox="0 0 128 96" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id={tileId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6366f1" />
          <stop offset="1" stopColor="#3730a3" />
        </linearGradient>
        <radialGradient id={discId} cx="50%" cy="45%" r="55%">
          <stop offset="0" stopColor="#ececfb" />
          <stop offset="1" stopColor="#ececfb" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="92" cy="42" r="40" fill={`url(#${discId})`} />
      <rect x="44" y="22" width="60" height="54" rx="13" fill={`url(#${tileId})`} transform="rotate(-8 74 49)" />
      {variant === 'overview' && (
        <g transform="rotate(-8 74 49)" stroke="#fff" strokeWidth="2.4" strokeLinecap="round">
          <line x1="60" y1="62" x2="60" y2="52" />
          <line x1="74" y1="62" x2="74" y2="42" />
          <line x1="88" y1="62" x2="88" y2="34" />
          <line x1="54" y1="62.5" x2="94" y2="62.5" strokeWidth="2" />
        </g>
      )}
      {variant === 'structure' && (
        <g transform="rotate(-8 74 49)" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
          <rect x="56" y="34" width="14" height="14" rx="3" />
          <rect x="78" y="34" width="14" height="14" rx="3" />
          <rect x="56" y="52" width="14" height="14" rx="3" />
          <rect x="78" y="52" width="14" height="14" rx="3" />
        </g>
      )}
      {variant === 'action' && (
        <g transform="rotate(-8 74 49)" stroke="#fff" strokeWidth="2.3" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="74" cy="49" r="13" />
          <circle cx="74" cy="49" r="6.5" />
          <circle cx="74" cy="49" r="1.4" fill="#fff" stroke="none" />
        </g>
      )}
      <circle cx="40" cy="20" r="5" fill="#c9bef4" />
      <circle cx="112" cy="72" r="4" fill="#a593ec" />
      <circle cx="30" cy="62" r="3" fill="#dcd4f8" />
    </svg>
  );
}

function PageHeader({ section, subtitle, illus }) {
  return (
    <header className="v5r-page-head" style={{ borderBottom: 'none', marginBottom: 0 }}>
      <div>
        <h1>
          AI领导力测评报告（基础版）
          <span className="v5r-divider">|</span>
          <span className="v5r-section-name">{section}</span>
        </h1>
        <div className="v5r-sub">{subtitle}</div>
      </div>
      <div className="v5r-head-illus">
        <HeaderIllus variant={illus} />
      </div>
    </header>
  );
}

function MetaBar({ meta }) {
  return (
    <div className="v5r-meta">
      <MetaItem icon="users" label="测评人" value={meta.nickname} />
      {meta.position && <MetaItem icon="user-check" label="岗位" value={meta.position} />}
      <MetaItem icon="award" label="版本" value={meta.version_label} />
      <MetaItem icon="clipboard" label="作答" value={`${meta.answers_done}/${meta.answers_total}`} />
      <MetaItem icon="book-open" label="生成时间" value={meta.generated_at} />
    </div>
  );
}

function MetaItem({ icon, label, value }) {
  return (
    <span className="v5r-meta-item">
      <span className="v5r-meta-icon"><ReportIcon name={icon} size={13} /></span>
      <span className="v5r-meta-label">{label}：</span>
      <span className="v5r-meta-val">{value || '—'}</span>
    </span>
  );
}

// ===========================================================================
// Page 1 · 结果总览
// ===========================================================================
function Page1({ vm }) {
  return (
    <section className="v5r-page" id="v5r-sec-overview">
      <PageHeader
        section="结果总览"
        subtitle="基于前哨 AI 领导力模型生成"
        illus="overview"
      />
      <MetaBar meta={vm.meta} />

      {/* 上半区：总体等级 + 一句话画像 + 能力摘要 */}
      <div className="v5r-p1-top" style={{ marginTop: 22 }}>
        <OverallCard vm={vm} />
        <div className="v5r-p1-right">
          <HeadlineCard headline={vm.overall.headline_tag} />
          <SummaryCard strengths={vm.strength_abilities} improvements={vm.improvement_abilities} />
          <KeyFindingsCard findings={vm.key_findings} />
        </div>
      </div>

      {/* 优先行动 */}
      <div className="v5r-card v5r-actions-card">
        <div className="v5r-card-title">
          <ReportIcon name="award" size={16} /> 优先行动
        </div>
        <div className="v5r-actions-chips">
          {vm.priority_actions.map((label, i) => (
            <div className="v5r-action-chip" key={i}>
              <ReportIcon name={['users', 'shield', 'puzzle'][i] || 'circle'} size={18} />
              {label}
            </div>
          ))}
        </div>
        <div className="v5r-action-foot">
          <ReportIcon name="lightbulb" size={14} />
          建议先从低风险、高频任务开始练习。
        </div>
      </div>
    </section>
  );
}

// 时期分布（升序 初用→重构，与 gauge 同色带）；count 来自 5 力各自所处时期
const PHASE_DEFS_ASC = [
  { idx: 1, label: '初用期' },
  { idx: 2, label: '惯用期' },
  { idx: 3, label: '扩散期' },
  { idx: 4, label: '重构期' },
];

function phaseDistribution(abilities) {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0 };
  (abilities || []).forEach((a) => { counts[abilityPhaseIdx(a)] += 1; });
  return counts;
}

// 0-100 综合成熟度指数仪表条：填充宽度 = percentage，色带按所处时期，25/50/75 刻度分隔 4 时期
function OverallGauge({ percentage, phaseIdx }) {
  const pct = Math.max(0, Math.min(100, Number(percentage) || 0));
  return (
    <div className="v5r-gauge">
      <div className="v5r-gauge-head">
        <span className="v5r-gauge-label">综合成熟度</span>
        <span className="v5r-gauge-num">
          <b>{pct}</b><span className="v5r-gauge-of"> / 100</span>
        </span>
      </div>
      <div className="v5r-gauge-track">
        <span className="v5r-gauge-tick" style={{ left: '25%' }} />
        <span className="v5r-gauge-tick" style={{ left: '50%' }} />
        <span className="v5r-gauge-tick" style={{ left: '75%' }} />
        <div className={`v5r-gauge-fill p${phaseIdx}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// 最强 / 最弱力点名行；最弱力额外标「突破口」
function ForceLine({ kind, ability }) {
  if (!ability) return null;
  const isWeak = kind === 'weak';
  return (
    <div className={`v5r-force-line ${isWeak ? 'weak' : 'strong'}`}>
      <span className="v5r-force-line-tag">{isWeak ? '最弱' : '最强'}</span>
      <span className="v5r-force-line-name">{ability.name}</span>
      {ability.l_grade_label && (
        <span className="v5r-force-line-phase">· {ability.l_grade_label}</span>
      )}
      {isWeak && <span className="v5r-force-line-break">突破口</span>}
    </div>
  );
}

// 时期分布小条：5 力按所处时期分段堆叠 + 计数图例
function PhaseDistribution({ abilities }) {
  const counts = phaseDistribution(abilities);
  const total = PHASE_DEFS_ASC.reduce((s, p) => s + counts[p.idx], 0);
  if (!total) return null;
  return (
    <div className="v5r-dist">
      <div className="v5r-dist-head">时期分布</div>
      <div className="v5r-dist-bar">
        {PHASE_DEFS_ASC.map((p) => (
          counts[p.idx] > 0 && (
            <div
              key={p.idx}
              className={`v5r-dist-seg p${p.idx}`}
              style={{ flexGrow: counts[p.idx] }}
            />
          )
        ))}
      </div>
      <div className="v5r-dist-legend">
        {PHASE_DEFS_ASC.map((p) => (
          <span key={p.idx} className="v5r-dist-leg-item">
            <i className={`p${p.idx}`} />{p.label}
            <b>{counts[p.idx]}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

function OverallCard({ vm }) {
  const o = vm.overall;
  const gaugeIdx = L_TO_IDX[o.l_grade] || abilityPhaseIdx({ percentage: o.percentage });
  return (
    <div className="v5r-card v5r-overall-card">
      <div>
        <div className="v5r-card-title">
          <ReportIcon name="shield" size={16} /> 总体等级
        </div>
        <div className="v5r-l-row">
          <div className="v5r-l-grade">{o.l_grade || '—'}</div>
          <div className="v5r-l-name">{o.l_grade_label}</div>
        </div>

        <OverallGauge percentage={o.percentage} phaseIdx={gaugeIdx} />

        {(o.strongest || o.weakest) && (
          <div className="v5r-force-lines">
            <ForceLine kind="strong" ability={o.strongest} />
            <ForceLine kind="weak" ability={o.weakest} />
          </div>
        )}

        <PhaseDistribution abilities={vm.abilities} />
      </div>
      {o.summary && (
        <div className="v5r-overall-tip">
          <ReportIcon name="lightbulb" size={16} />
          <span>{o.summary}</span>
        </div>
      )}
    </div>
  );
}

function HeadlineCard({ headline }) {
  return (
    <div className="v5r-card v5r-headline-card">
      <div className="v5r-card-title">
        <ReportIcon name="quote" size={16} /> 一句话画像
      </div>
      <div className="v5r-headline-text">{headline || '—'}</div>
    </div>
  );
}

// Page 1 右栏第三卡「关键发现」（2026-06-04）。findings 为 3 条点状观察；
// 旧报告无该字段 → 空数组 → 整卡不渲染（lenient，避免旧数据出现空卡）。
function KeyFindingsCard({ findings }) {
  if (!findings || findings.length === 0) return null;
  return (
    <div className="v5r-card">
      <div className="v5r-card-title">
        <ReportIcon name="lightbulb" size={16} /> 关键发现
      </div>
      <ul className="v5r-find-list">
        {findings.map((text, i) => (
          <li key={i}>{text}</li>
        ))}
      </ul>
    </div>
  );
}

function SummaryCard({ strengths, improvements }) {
  return (
    <div className="v5r-card">
      <div className="v5r-card-title">
        <ReportIcon name="bar-chart-3" size={16} /> 能力摘要
      </div>
      <div className="v5r-summary-grid">
        <div className="v5r-summary-col strength">
          <h4><ReportIcon name="thumbs-up" size={13} /> 优势能力</h4>
          <ul>
            {strengths.map((a) => (
              <li key={a.code}>{a.name}</li>
            ))}
          </ul>
        </div>
        <div className="v5r-summary-col improve">
          <h4><ReportIcon name="trending-up" size={13} /> 优先提升能力</h4>
          <ul>
            {improvements.map((a) => (
              <li key={a.code}>{a.name}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Page 2 · 能力结构
// ===========================================================================
function Page2({ vm }) {
  return (
    <section className="v5r-page" id="v5r-sec-structure">
      <PageHeader
        section="能力结构"
        subtitle="从 5 项核心能力出发，看清你的 AI 应用能力图谱与成熟度分布"
        illus="structure"
      />
      <MetaBar meta={vm.meta} />
      <div className="v5r-p2-grid" style={{ marginTop: 22 }}>
        <div className="v5r-card v5r-p2-radar-card">
          <div className="v5r-card-title">
            <ReportIcon name="bar-chart-3" size={16} /> 5 力成熟度图谱
          </div>
          <ReportV5Radar abilities={vm.abilities} />
        </div>
        <div className="v5r-card">
          <div className="v5r-card-title">
            <ReportIcon name="trending-up" size={16} /> 成熟度跃迁阶梯
          </div>
          <MaturityLadder abilities={vm.abilities} />
        </div>
      </div>
    </section>
  );
}

// 成熟度跃迁阶梯：把 5 力按所在时期摞成 4 级台阶（重构→初用），底部给出突破口提示。
const LADDER_PHASES = [
  { idx: 4, label: '重构期', range: '75–100' },
  { idx: 3, label: '扩散期', range: '50–75' },
  { idx: 2, label: '惯用期', range: '25–50' },
  { idx: 1, label: '初用期', range: '0–25' },
];
const L_TO_IDX = { L1: 1, L2: 2, L3: 3, L4: 4 };

// 时期序号：优先取 l_grade（权威），缺失时按 percentage 分位兜底
function abilityPhaseIdx(a) {
  const fromGrade = L_TO_IDX[a?.l_grade];
  if (fromGrade) return fromGrade;
  const pct = Math.max(0, Math.min(100, Number(a?.percentage) || 0));
  if (pct < 25) return 1;
  if (pct < 50) return 2;
  if (pct < 75) return 3;
  return 4;
}

function MaturityLadder({ abilities }) {
  const byPhase = { 1: [], 2: [], 3: [], 4: [] };
  abilities.forEach((a) => { byPhase[abilityPhaseIdx(a)].push(a); });
  const topCount = byPhase[3].length + byPhase[4].length;
  // 已达到的最高时期：低于它的空时期是「已跃迁」(越过)，高于它的才是「尚未触及」
  const reachedIdx = [4, 3, 2, 1].find((i) => byPhase[i].length > 0) || 0;
  // 突破口：得分最低的一项
  const weakest = abilities.reduce(
    (lo, a) => (lo && (lo.percentage || 0) <= (a.percentage || 0) ? lo : a),
    null,
  );

  return (
    <>
      <div className="v5r-ladder">
        {LADDER_PHASES.map((p) => {
          const forces = byPhase[p.idx];
          const empty = forces.length === 0;
          const passed = empty && p.idx < reachedIdx; // 已越过该阶段
          return (
            <div key={p.idx} className={`v5r-rung p${p.idx}${empty ? (passed ? ' passed' : ' empty') : ''}`}>
              <div className="v5r-rung-level">
                <span className="v5r-rung-name"><i />{p.label}</span>
                <span className="v5r-rung-range">{p.range}</span>
              </div>
              <div className="v5r-rung-forces">
                {empty ? (
                  <span className="v5r-rung-none">{passed ? '已跃迁' : '尚未触及'}</span>
                ) : (
                  forces.map((a) => (
                    <span key={a.code} className="v5r-force-tag">
                      {a.name}
                      <span className="v5r-force-score">{a.percentage}</span>
                    </span>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
      {weakest && (
        <div className="v5r-ladder-note">
          <ReportIcon name="lightbulb" size={16} />
          <span>
            5 力中已有 <b>{topCount}</b> 项进入扩散期及以上；当前最需要补强的是{' '}
            <b>{weakest.name}</b>
            （{weakest.l_grade_label || ''}{Number.isFinite(weakest.percentage) ? ` ${weakest.percentage}` : ''}），
            把它往上推一阶，整体会更稳地迈向更高时期。
          </span>
        </div>
      )}
    </>
  );
}

// ===========================================================================
// Page 3 · 行动建议
// ===========================================================================
function Page3({ vm }) {
  return (
    <section className="v5r-page" id="v5r-sec-action">
      <PageHeader
        section="行动建议"
        subtitle="结合测评结果，给出适合你的成长路径、具体场景与提升建议"
        illus="action"
      />
      <MetaBar meta={vm.meta} />

      <div className="v5r-interp-card" style={{ marginTop: 22 }}>
        <div className="v5r-card-title">
          <ReportIcon name="book-open" size={16} /> 综合解读
        </div>
        <p className="v5r-interp-text">{vm.comprehensive_interpretation || '—'}</p>
      </div>

      <div className="v5r-scen-grid">
        <ScenarioCard
          variant="try-first"
          title="适合优先尝试的 AI 场景"
          icon="thumbs-up"
          items={vm.try_first_scenarios}
          footer="建议优先从低风险、高频、可复核的任务开始练习。"
        />
        <ScenarioCard
          variant="cautious"
          title="需要人工深度介入的管理场景"
          icon="shield"
          items={vm.be_cautious_scenarios}
          footer="涉及数据、事实、敏感信息和正式决策时，务必保留人工复核。"
        />
      </div>

      <div className="v5r-improvements">
        {vm.priority_improvements.map((it, i) => (
          <ImprovementCard key={i} item={it} num={i + 1} />
        ))}
      </div>

    </section>
  );
}

function ScenarioCard({ variant, title, icon, items, footer }) {
  return (
    <div className={`v5r-card v5r-scen-card ${variant}`}>
      <div className={`v5r-card-title ${variant}`}>
        <ReportIcon name={icon} size={16} /> {title}
      </div>
      <div className="v5r-scen-list">
        {items.map((s, i) => (
          <div key={i} className="v5r-scen-item">
            <ReportIcon name={s.icon_hint || 'circle'} size={16} />
            {s.name}
          </div>
        ))}
      </div>
      <div className="v5r-scen-foot">
        <ReportIcon name="lightbulb" size={13} />
        {footer}
      </div>
    </div>
  );
}

function ImprovementCard({ item, num }) {
  return (
    <div className="v5r-improv-card">
      <div className="v5r-improv-head">
        <span className="v5r-improv-num">0{num}</span>
        <span className="v5r-improv-title">{item.title}</span>
      </div>
      <div className="v5r-improv-block">
        <span className="v5r-improv-block-label">为什么要做</span>
        {item.why_to_do}
      </div>
      <div className="v5r-improv-block">
        <span className="v5r-improv-block-label">可以怎么做</span>
        {item.how_to_do}
      </div>
    </div>
  );
}

// ===========================================================================
// 报告末尾：查看 / 解锁专业版报告 CTA
// 下载按钮已抽到 ReportShell 的目录底部（ReportDownloadButton variant=rough）；
// 这张卡片是「查看专业版报告」的现有入口，目录底部的引导文字指向它。
// ===========================================================================
function DetailedUnlockCTA({ unlocked, onUnlock }) {
  return (
    <>
      {unlocked ? (
        // 已解锁（backend 顶层 is_detailed_unlocked=true）—— "查看"按钮直接进 detailed 页
        <div className="v5r-cta unlocked">
          <div>
            <h3>详细报告已解锁</h3>
            <p>完整专业版报告已就绪 —— 含 5 力深度诊断、管理场景应用建议与 30 天提升路径。</p>
          </div>
          <button className="v5r-cta-btn" type="button" onClick={onUnlock}>
            <ReportIcon name="book-open" size={16} /> 查看详细报告
          </button>
        </div>
      ) : (
        // 未解锁 —— "解锁"按钮（真接口下 backend 会 403，付费流由独立 ticket 接入）
        <div className="v5r-cta">
          <div>
            <h3>想看更深入的解读？解锁详细报告</h3>
            <p>详细报告会按你的岗位给出跨能力关联分析、5 力深度诊断、管理场景应用建议与 30 天提升路径。</p>
          </div>
          <button className="v5r-cta-btn" type="button" onClick={onUnlock}>
            <ReportIcon name="award" size={16} /> 解锁详细报告
          </button>
        </div>
      )}
    </>
  );
}
